import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import LatencyCard from "../LatencyCard";
import ModelTab from "./ModelTab";
import TranscriberTab from "./TranscriberTab";
import VoiceTab from "./VoiceTab";
import TelephonyTab from "./TelephonyTab";
import RegionTab from "./RegionTab";
import CallLimitsTab from "./CallLimitsTab";
import BargeInTab from "./BargeInTab";
import LanguageTab from "./LanguageTab";
import { useLanguageConflicts } from "../languageSetup";
import { isRealtimeModel } from "@/utils/realtimeModels";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

interface AgentTabProps {
  // The assistant's organization, for the Region section's organization default.
  organizationId?: string;
  // The saved assistant, for the latency card. Left out for a new assistant: no card.
  assistantId?: string | null;
  // The open accordion item. Leave both out and the tab keeps its own state.
  openSection?: string;
  onOpenSectionChange?: (section: string) => void;
}

const ConflictMark = () => (
  <span className="ml-2 text-xs font-medium text-destructive" aria-label="Language conflict">
    Language conflict
  </span>
);

export default function AgentTab({ organizationId, assistantId, openSection, onOpenSectionChange }: AgentTabProps = {}) {
  const [ownSection, setOwnSection] = useState("");
  const { watch } = useFormContext();
  const showLanguage = !isRealtimeModel(watch("llm_provider"), watch("llm_model"));
  const conflicts = useLanguageConflicts();
  const sttConflict = conflicts.some((c) => c.section === "transcriber");
  const voiceConflict = conflicts.some((c) => c.section === "voice");
  const controlled = onOpenSectionChange !== undefined;

  return (
    <Card className="border-none shadow-none bg-transparent">
      {assistantId && showLanguage && (
        <LatencyCard assistantId={assistantId} organizationId={organizationId} />
      )}
      <Accordion
        type="single"
        collapsible
        className="w-full space-y-4"
        value={controlled ? openSection ?? "" : ownSection}
        onValueChange={controlled ? onOpenSectionChange : setOwnSection}
      >
        {showLanguage && (
          <AccordionItem value="language" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Language Configuration</h3>
                <p className="text-sm text-muted-foreground">
                  Choose the language or languages this assistant speaks
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <LanguageTab />
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="model" className="border rounded-lg bg-card shadow-card">
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">Model Configuration</h3>
              <p className="text-sm text-muted-foreground">
                Configure the language model and basic settings for your
                assistant
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ModelTab />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="stt_provider"
          className={`border rounded-lg bg-card shadow-card ${sttConflict ? "border-destructive" : ""}`}
        >
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">
                Transcriber Configuration
                {sttConflict && <ConflictMark />}
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure speech-to-text capabilities for your assistant using
                providers like OpenAI, Deepgram, Groq, and more
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <TranscriberTab />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="voice"
          className={`border rounded-lg bg-card shadow-card ${voiceConflict ? "border-destructive" : ""}`}
        >
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">
                Voice Configuration
                {voiceConflict && <ConflictMark />}
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure ElevenLabs text-to-speech capabilities for your
                assistant
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <VoiceTab />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="telephony" className="border rounded-lg bg-card shadow-card">
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">Telephony Configuration</h3>
              <p className="text-sm text-muted-foreground">
                Choose the carrier that places this assistant's V2 phone calls
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <TelephonyTab />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="region" className="border rounded-lg bg-card shadow-card">
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">Region</h3>
              <p className="text-sm text-muted-foreground">
                Choose the region of the server that places this assistant's V2 calls,
                or follow the organization default
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <RegionTab organizationId={organizationId} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="call-limits" className="border rounded-lg bg-card shadow-card">
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">Call Limits</h3>
              <p className="text-sm text-muted-foreground">
                How often this assistant may call one phone number (V2 calls)
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <CallLimitsTab organizationId={organizationId} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="barge-in" className="border rounded-lg bg-card shadow-card">
          <AccordionTrigger className="px-4">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold">Interruptions</h3>
              <p className="text-sm text-muted-foreground">
                How the assistant handles being interrupted by the caller
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <BargeInTab />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}
